import { useRef } from 'react';
import { CHART_COLORS } from '../../process-analysis.constants';
import type { LineItem } from './line-chart.type';

/**
 * Hook that provides stable color assignment for chart items.
 * Items retain their colors when re-selected or when the chart is re-rendered.
 *
 * @param {LineItem[]} selectedItems - The items to assign colors to
 * @returns {Record<string, string>} A map of item IDs to colors
 */
const useItemColors = (selectedItems: LineItem[]): Record<string, string> => {
  const colorMapRef = useRef<Map<string, string>>(new Map());

  const currentIds = selectedItems.map((item) => item.id);
  const colorMap = colorMapRef.current;

  // Clean up colors for deselected items
  for (const [itemId] of colorMap.entries()) {
    if (!currentIds.includes(itemId)) {
      colorMap.delete(itemId);
    }
  }

  // Assign colors to items that don't have one
  const usedColors = new Set(colorMap.values());

  for (const item of selectedItems) {
    if (!colorMap.has(item.id)) {
      // Find first available color
      let color = CHART_COLORS.find((c) => !usedColors.has(c));

      // If all colors are used, cycle through them
      if (!color) {
        color = CHART_COLORS[usedColors.size % CHART_COLORS.length];
      }

      colorMap.set(item.id, color);
      usedColors.add(color);
    }
  }

  // Return colors as object
  return Object.fromEntries(selectedItems.map((item) => [item.id, colorMap.get(item.id)!]));
};

export { useItemColors };
